/**
 * Utility functions for getting optimized image URLs on the client side.
 *
 * These functions mirror the server-side logic in imageOptimization.ts
 * to generate the correct URLs for pre-resized images stored in R2.
 */

export const IMAGE_SIZES = {
  thumb: { width: 500, quality: 70 },
  medium: { width: 800, quality: 80 },
  original: { width: 1000, quality: 80 },
} as const;

export type ImageSizeKey = keyof typeof IMAGE_SIZES;

/**
 * Parse the image path to extract base path and filename
 */
function parseImagePath(imageUrl: string): {
  basePath: string;
  filename: string;
} {
  const url = imageUrl.replace('https://media.easy-peasy.ai/', '');
  const lastSlashIndex = url.lastIndexOf('/');
  const basePath = lastSlashIndex >= 0 ? url.substring(0, lastSlashIndex) : '';
  const fullFilename =
    lastSlashIndex >= 0 ? url.substring(lastSlashIndex + 1) : url;

  const lastDotIndex = fullFilename.lastIndexOf('.');
  const filename =
    lastDotIndex >= 0 ? fullFilename.substring(0, lastDotIndex) : fullFilename;

  return { basePath, filename };
}

/**
 * Get the URL for a pre-resized image stored in R2
 *
 * Returns direct R2 URL for pre-resized images (created on TOGGLE_PUBLIC).
 *
 * @param imageUrl - The original image URL (e.g., https://media.easy-peasy.ai/userId/uuid.png)
 * @param size - The size key ('thumb' or 'medium')
 * @returns The URL for the pre-resized WebP version
 */
export function getOptimizedImageUrl(
  imageUrl: string,
  size: ImageSizeKey,
): string {
  if (!imageUrl) return '';

  if (size === 'original') {
    return imageUrl;
  }

  // Only process images from our media domain
  if (!imageUrl.includes('media.easy-peasy.ai')) {
    return imageUrl; // Return original URL for external images
  }

  const { basePath, filename } = parseImagePath(imageUrl);
  const key = basePath
    ? `${basePath}/${filename}_${size}.webp`
    : `${filename}_${size}.webp`;

  return `https://media.easy-peasy.ai/${key}`;
}

/**
 * Get the URL with fallback to on-demand resize API
 *
 * @deprecated Use getOptimizedImageUrl() instead
 */
export function getOptimizedImageUrlWithFallback(
  imageUrl: string,
  size: ImageSizeKey,
): string {
  return getOptimizedImageUrl(imageUrl, size);
}

/**
 * Legacy function - returns Cloudflare CDN-CGI URL
 * Use this only for images that absolutely need dynamic resizing
 */
export function getCdnCgiUrl(
  imageUrl: string,
  width: number,
  quality: number = 80,
  format: 'auto' | 'jpeg' | 'webp' = 'auto',
): string {
  if (!imageUrl) return '';
  return `https://easy-peasy.ai/cdn-cgi/image/quality=${quality},format=${format},width=${width}/${imageUrl}`;
}
